import React, { FC, Ref } from 'react'
import MobileSwipe from 'components/MobileSwipe'
import usePreviousState from 'hooks/previousState'
import BackIcon from 'svg/caret-left.svg'
import NextIcon from 'svg/caret-right.svg'
import { Controls, Gallery } from './styles'
import { GALLERY_MAX_WIDTH_DESKTOP } from '../../selectors/gallery'
import Item from './Item'
import {
  GALLERY_ITEM_TYPE_ARTIST_VIDEO,
  GALLERY_ITEM_TYPE_IMAGE,
  GALLERY_ITEM_TYPE_VIEW_IN_A_ROOM_IMAGE,
} from '../../constants'
import { GalleryItem } from '../../types'

type GalleryComponentProps = {
  carouselHook: Record<string, any>
  clickImage: (...args: Array<any>) => any
  currentTab: string
  dimensions: { width: number; height: number }
  galleryRef: Ref<HTMLDivElement | null>
  galleryItems: Array<GalleryItem>
  isAdmin: boolean
  selectedProduct: Record<string, any>
  selectedFrameOption: Record<string, any>
}

const GalleryComponent: FC<GalleryComponentProps> = (props) => {
  const {
    carouselHook,
    clickImage,
    currentTab,
    dimensions,
    galleryRef,
    galleryItems,
    isAdmin,
    selectedProduct,
    selectedFrameOption,
  } = props
  const previousTab = usePreviousState(currentTab)
  const { width: widthInPixels, height: heightInPixels } = dimensions
  const { index, setIndex, prev, next } = carouselHook
  const element =
    (typeof galleryRef === 'object' &&
      galleryRef?.current?.getBoundingClientRect?.()?.toJSON?.()) ||
    {}
  const elementWidth = element.width || 0
  const offset = elementWidth * index
  const currentGalleryItem = galleryItems[index || 0]
  // We need to handle switching between Tabs/Products ( hideAnimations )
  const disableAnimations = previousTab !== currentTab

  const getGalleryAspectRatio = () => {
    const { galleryItemType } = currentGalleryItem

    if (galleryItemType === GALLERY_ITEM_TYPE_VIEW_IN_A_ROOM_IMAGE) {
      return {
        aspectRatio: '1',
      }
    }

    const { width: itemWidthInPixels, height: itemHeightInPixels } = currentGalleryItem

    if (galleryItemType === GALLERY_ITEM_TYPE_IMAGE && currentGalleryItem.type === 'print') {
      const smallBorderSizeInInches = 2 // inches for Prints width < 25

      const largeBorderSizeInInches = 3 // inches for Prints width >= 25

      const frameBorder = 0.625 // inches

      const frameColor = selectedFrameOption?.title
      const { width: printWidthInInches, height: printHeightInInches } = selectedProduct
      const scaleBy =
        printWidthInInches / printHeightInInches >
        Number(itemWidthInPixels) / Number(itemHeightInPixels)
          ? 'width'
          : 'height'
      const longestSideInInches = Number(
        scaleBy === 'width' ? printWidthInInches : printHeightInInches
      )
      const matBorderSizeInInches =
        printWidthInInches < 18 ? smallBorderSizeInInches : largeBorderSizeInInches
      const totalSizeInInches =
        longestSideInInches + matBorderSizeInInches * 2 + (frameColor ? frameBorder * 2 : 0)
      const galleryComponentWidthInPixels =
        itemWidthInPixels < widthInPixels ? itemWidthInPixels : GALLERY_MAX_WIDTH_DESKTOP // Have to use the MAX_WIDTH_DESKTOP for desktop and mobile if it is wider to keep consistency in aspect ratio
      const inchToPixel =
        (scaleBy === 'width' ? Number(galleryComponentWidthInPixels) : Number(itemHeightInPixels)) /
        totalSizeInInches
      const matSize = inchToPixel * matBorderSizeInInches
      const frameSize = inchToPixel * frameBorder

      return {
        aspectRatio: `${
          galleryComponentWidthInPixels /
          (itemHeightInPixels + Math.round(matSize) + Math.round(frameSize))
        }`,
      }
    }

    return {
      aspectRatio: `${itemWidthInPixels / itemHeightInPixels}`,
    }
  }

  return (
    <>
      <Gallery
        data-section='gallery'
        ref={galleryRef}
        width={widthInPixels}
        height={heightInPixels}
        style={getGalleryAspectRatio()}
        {...(disableAnimations
          ? {
              disableAnimations,
            }
          : null)}
        {...(isAdmin
          ? {
              isAdmin,
            }
          : null)}
      >
        <MobileSwipe
          minimumDragDistance={50}
          onSwipeComplete={(direction) => {
            direction === 'next' ? next() : prev()
          }}
          onClick={clickImage}
        />

        <div
          data-section='items'
          style={{
            transform: `translateX(-${offset}px)`,
          }}
        >
          {galleryItems.map((galleryItem, key) => {
            const { galleryItemType } = galleryItem
            return (
              <button
                type='button'
                key={key}
                data-type='item'
                onClick={
                  galleryItemType !== GALLERY_ITEM_TYPE_ARTIST_VIDEO ? clickImage : undefined
                }
                {...(index === key
                  ? {
                      'data-style': 'active',
                    }
                  : {})}
              >
                <Item {...{ ...galleryItem, key, galleryRef }} hasHighFetchPriority={key === 0} />
              </button>
            )
          })}
        </div>
      </Gallery>

      {galleryItems.length > 1 && (
        <Controls data-section='controls' height={heightInPixels}>
          <BackIcon data-type='back' onClick={prev} />

          <div data-section='dots'>
            {galleryItems.map((data, key) => (
              <button
                data-type='dot'
                type='button'
                key={key}
                onClick={() => setIndex(key)}
                {...(index === key
                  ? {
                      'data-style': 'active',
                    }
                  : {})}
              />
            ))}
          </div>

          <NextIcon data-type='next' onClick={next} />
        </Controls>
      )}
    </>
  )
}

export default GalleryComponent
